/**
 * Forced block outcomes — which of BLOCKED, DEGRADED or REDIRECTED a trigger is
 * presented as.
 *
 * Eleven of the fourteen triggers have no outcome assigned in any source (the
 * schema says two, the prototype's own matrix names three — see
 * `blocked-state.component.ts`), so every default in the catalogue is a guess
 * carrying an `unspecified()` marker. Being able to show Orange the same trigger
 * as all three, in one session, without a code change, is worth more than the
 * guess: the question "which of these should a non-administrator see?" is much
 * easier to answer while looking at the three answers.
 *
 * ### Why this is in `core/dev` and not in `dev/`
 *
 * `orange-blocked-state` has seven call sites — the shell, S-01, S-03, S-04, S-05,
 * S-07 and S-12 — and four of them resolve their own trigger locally rather than
 * receiving an `EligibilityBlock` from the BFF. Threading an override through seven
 * templates would mean seven chances to forget one, and a switch that silently
 * works on three screens out of seven is worse than no switch. So the component
 * consults this store itself, and the call sites do not know it exists.
 *
 * Same arrangement as [[dev-mode]]: POC scaffolding that product code has to be
 * able to read, kept in `core/dev/` where it is obvious what it is. It is inert
 * until something calls `set()`, and the only things that do are in `src/app/dev/`.
 *
 * ### It is not the whole mechanism
 *
 * `EligibilityBlock.outcome` arrives on the wire, because in production the BFF
 * decides it; the mock BFF has its own `BlockOutcomeOverrides` for that path, and
 * `?block=` sets both. Two reasons that matters rather than being redundant:
 * `assistedSalesPhone` is only populated when the *wire* outcome is REDIRECTED, so
 * a client-only override would produce a redirect with no number to call; and the
 * eligibility chain's answer is what `docs/DEMO-MATRIX.md` and the E2E specs read.
 */
import { Injectable, computed, signal } from '@angular/core';
import type { BlockOutcome, BlockReason } from '../../../generated/graphql';

@Injectable({ providedIn: 'root' })
export class BlockOutcomes {
  private readonly forcedSignal = signal<ReadonlyMap<BlockReason, BlockOutcome>>(new Map());

  /** Read by the component, so flipping a switch re-renders an open block. */
  readonly forced = this.forcedSignal.asReadonly();

  /** For the dev overlay's own status line. Nothing in the product path reads it. */
  readonly anyForced = computed(() => this.forcedSignal().size > 0);

  outcomeFor(reason: BlockReason): BlockOutcome | null {
    return this.forcedSignal().get(reason) ?? null;
  }

  set(reason: BlockReason, outcome: BlockOutcome): void {
    const next = new Map(this.forcedSignal());
    next.set(reason, outcome);
    this.forcedSignal.set(next);
  }

  /** Back to the catalogue's default for one trigger. */
  release(reason: BlockReason): void {
    const next = new Map(this.forcedSignal());
    next.delete(reason);
    this.forcedSignal.set(next);
  }

  clear(): void {
    this.forcedSignal.set(new Map());
  }
}
